import MiniSearch, { type SearchResult } from "minisearch";

import { BRAND_SEARCH_ALIASES } from "./search-aliases";
import {
  compactSearch,
  foldSearchVowels,
  normalizeSearch,
  queryVariants,
  type SearchQueryVariant,
  transliterateCyrillic,
} from "./search-normalization";
import type { ProductView } from "./types";
import { productMatchesIntent, productSearchIntentAliases, resolveSearchIntent } from "./search-intents";
import { parseProductSearchQuery } from "./search-query";
import { productMatchesSearchBrand, qualifiedBrandQuery } from "./search-brands";

export interface ProductSearchDocument {
  id: string;
  nameRu: string;
  nameRo: string;
  nameUk: string;
  brand: string;
  phonetic: string;
  category: string;
  package: string;
  store: string;
  catalog: string;
  notes: string;
  numeric: string;
}

export interface ProductSearchMatches {
  scores: ReadonlyMap<string, number>;
  usedFuzzy: boolean;
}

const SEARCH_FIELDS = [
  "nameRu",
  "nameRo",
  "nameUk",
  "brand",
  "phonetic",
  "category",
  "package",
  "store",
  "catalog",
  "notes",
  "numeric",
] as const;

const FUZZY_SEARCH_FIELDS = ["nameRu", "nameRo", "nameUk", "brand", "store"];

const SEARCH_BOOSTS: Readonly<Record<(typeof SEARCH_FIELDS)[number], number>> = {
  nameRu: 9,
  nameRo: 9,
  nameUk: 9,
  brand: 8,
  phonetic: 7,
  category: 4,
  package: 3,
  store: 2,
  catalog: 1.5,
  notes: 1,
  numeric: 5,
};

let searchIndexes = new WeakMap<readonly ProductView[], MiniSearch<ProductSearchDocument>>();
let pendingSearchIndexes = new WeakMap<
  readonly ProductView[],
  Promise<MiniSearch<ProductSearchDocument>>
>();
let fallbackWarningShown = false;
let queryCaches = new WeakMap<readonly ProductView[], Map<string, ProductSearchMatches>>();

export function productSearchKey(product: ProductView): string {
  return `${product.catalog_id}\u0000${product.id}`;
}

function textWithVariants(...values: unknown[]): string {
  const variants = new Set<string>();
  for (const value of values) {
    const normalized = normalizeSearch(value);
    if (!normalized) {
      continue;
    }
    variants.add(normalized);
    const transliterated = transliterateCyrillic(normalized);
    if (transliterated !== normalized) {
      variants.add(transliterated);
    }
  }
  return Array.from(variants).join(" ");
}

function brandSearchText(product: ProductView): string {
  const normalizedBrand = normalizeSearch(product.brand);
  const aliases = BRAND_SEARCH_ALIASES[normalizedBrand] ?? [];
  const values = [product.brand, ...aliases];
  const variants = new Set<string>();
  for (const value of values) {
    const normalized = normalizeSearch(value);
    if (!normalized) {
      continue;
    }
    variants.add(normalized);
    const compact = compactSearch(normalized);
    if (compact.length >= 2) {
      variants.add(compact);
    }
    const transliterated = transliterateCyrillic(normalized);
    if (transliterated !== normalized) {
      variants.add(transliterated);
      variants.add(compactSearch(transliterated));
    }
  }
  return Array.from(variants).join(" ");
}

function numericSearchText(product: ProductView): string {
  const unit = product.unit_price;
  return textWithVariants(
    product.new_price_mdl?.toFixed(2),
    product.old_price_mdl?.toFixed(2),
    product.discount_percent,
    unit?.minimum_mdl.toFixed(2),
    unit?.maximum_mdl.toFixed(2),
  );
}

export function productSearchDocument(
  product: ProductView,
): ProductSearchDocument {
  return {
    id: productSearchKey(product),
    nameRu: textWithVariants(product.product_ru, ...productSearchIntentAliases(product)),
    nameRo: textWithVariants(product.product_ro),
    nameUk: textWithVariants(product.product_uk),
    brand: brandSearchText(product),
    phonetic: textWithVariants(
      foldSearchVowels(product.product_ru),
      foldSearchVowels(product.brand),
    ),
    category: textWithVariants(product.category_id, product.category_search_text),
    package: textWithVariants(product.weight_or_volume, product.unit_price?.basis),
    store: textWithVariants(product.store, product.catalog.chain),
    catalog: textWithVariants(product.catalog.title),
    notes: textWithVariants(product.note_ru, product.note_ro, product.note_uk),
    numeric: numericSearchText(product),
  };
}

function tokenize(value: string): string[] {
  return normalizeSearch(value).split(" ").filter(Boolean);
}

function emptySearchIndex(): MiniSearch<ProductSearchDocument> {
  return new MiniSearch<ProductSearchDocument>({
    fields: [...SEARCH_FIELDS],
    idField: "id",
    tokenize,
    processTerm: (term) => term,
  });
}

function buildSearchIndex(
  products: readonly ProductView[],
): MiniSearch<ProductSearchDocument> {
  const index = emptySearchIndex();
  index.addAll(products.map(productSearchDocument));
  return index;
}

export function prepareProductSearch(
  products: readonly ProductView[],
): Promise<MiniSearch<ProductSearchDocument>> {
  const cached = searchIndexes.get(products);
  if (cached) {
    return Promise.resolve(cached);
  }
  const pending = pendingSearchIndexes.get(products);
  if (pending) {
    return pending;
  }
  const index = emptySearchIndex();
  const promise = index
    .addAllAsync(products.map(productSearchDocument), { chunkSize: 100 })
    .then(() => {
      const completed = searchIndexes.get(products) ?? index;
      searchIndexes.set(products, completed);
      pendingSearchIndexes.delete(products);
      return completed;
    })
    .catch((error: unknown) => {
      pendingSearchIndexes.delete(products);
      throw error;
    });
  pendingSearchIndexes.set(products, promise);
  return promise;
}

function searchIndexFor(
  products: readonly ProductView[],
): MiniSearch<ProductSearchDocument> {
  const cached = searchIndexes.get(products);
  if (cached) {
    return cached;
  }
  const index = buildSearchIndex(products);
  searchIndexes.set(products, index);
  pendingSearchIndexes.delete(products);
  return index;
}

function fuzzyDistance(term: string): number | false {
  if (/^\d+(?:\.\d+)?$/u.test(term) || term.length <= 3) {
    return false;
  }
  if (term.length <= 5) {
    return 1;
  }
  if (term.length <= 8) {
    return 2;
  }
  return Math.min(3, Math.max(2, Math.floor(term.length * 0.22)));
}

function mergeResults(
  target: Map<string, number>,
  results: readonly SearchResult[],
  variant: SearchQueryVariant,
): void {
  const queryTerms = tokenize(variant.value);
  for (const result of results) {
    if (result.terms.length === 0) {
      continue;
    }
    const id = String(result.id);
    const distance = queryTerms.reduce(
      (total, queryTerm) =>
        total +
        Math.min(
          ...result.terms.map((matchedTerm) =>
            damerauLevenshtein(queryTerm, matchedTerm),
          ),
        ),
      0,
    );
    const comparedLength = Math.max(
      1,
      queryTerms.reduce((total, term) => total + term.length, 0),
    );
    const similarity = Math.max(0, 1 - distance / comparedLength);
    const score =
      similarity * 10_000 + variant.weight * 100 + Math.log1p(result.score);
    if (score > (target.get(id) ?? Number.NEGATIVE_INFINITY)) {
      target.set(id, score);
    }
  }
}

function damerauLevenshtein(left: string, right: string): number {
  if (left === right) {
    return 0;
  }
  if (!left) {
    return right.length;
  }
  if (!right) {
    return left.length;
  }
  const rows = Array.from({ length: left.length + 1 }, () =>
    Array<number>(right.length + 1).fill(0),
  );
  for (let row = 0; row <= left.length; row += 1) {
    rows[row]![0] = row;
  }
  for (let column = 0; column <= right.length; column += 1) {
    rows[0]![column] = column;
  }
  for (let row = 1; row <= left.length; row += 1) {
    for (let column = 1; column <= right.length; column += 1) {
      const substitutionCost = left[row - 1] === right[column - 1] ? 0 : 1;
      rows[row]![column] = Math.min(
        rows[row - 1]![column]! + 1,
        rows[row]![column - 1]! + 1,
        rows[row - 1]![column - 1]! + substitutionCost,
      );
      if (
        row > 1 &&
        column > 1 &&
        left[row - 1] === right[column - 2] &&
        left[row - 2] === right[column - 1]
      ) {
        rows[row]![column] = Math.min(
          rows[row]![column]!,
          rows[row - 2]![column - 2]! + 1,
        );
      }
    }
  }
  return rows[left.length]![right.length]!;
}

function runSearch(
  index: MiniSearch<ProductSearchDocument>,
  variants: readonly SearchQueryVariant[],
  fuzzy: boolean,
): Map<string, number> {
  const scores = new Map<string, number>();
  for (const variant of variants) {
    const results = index.search(variant.value, {
      fields: fuzzy ? FUZZY_SEARCH_FIELDS : [...SEARCH_FIELDS],
      boost: { ...SEARCH_BOOSTS },
      combineWith: "AND",
      prefix: (term) => term.length >= 2,
      fuzzy: fuzzy ? fuzzyDistance : false,
      maxFuzzy: 3,
      weights: {
        prefix: 0.72,
        fuzzy: 0.32,
      },
    });
    mergeResults(scores, results, variant);
  }
  return scores;
}

function fallbackSearch(
  products: readonly ProductView[],
  query: string,
): ProductSearchMatches {
  const terms = normalizeSearch(query).split(" ").filter(Boolean);
  const scores = new Map<string, number>();
  for (const product of products) {
    const document = productSearchDocument(product);
    const haystack = normalizeSearch(
      SEARCH_FIELDS.map((field) => document[field]).join(" "),
    );
    if (terms.every((term) => haystack.includes(term))) {
      scores.set(document.id, 1);
    }
  }
  return { scores, usedFuzzy: false };
}

function searchSingleProduct(
  products: readonly ProductView[],
  query: string,
): ProductSearchMatches {
  if (!normalizeSearch(query)) {
    return { scores: new Map(), usedFuzzy: false };
  }
  const branded = qualifiedBrandQuery(products, query);
  if (branded) {
    const typeMatches = searchSingleProduct(products, branded.productQuery);
    const scores = new Map<string, number>();
    for (const product of products) {
      const key = productSearchKey(product);
      const score = typeMatches.scores.get(key);
      if (score !== undefined && productMatchesSearchBrand(product, branded.brandKey)) scores.set(key, score);
    }
    return { scores, usedFuzzy: typeMatches.usedFuzzy };
  }
  try {
    const index = searchIndexFor(products);
    const variants = queryVariants(query);
    const intent = resolveSearchIntent(query);
    if (intent) {
      const lexical = runSearch(index, variants, false);
      const scores = new Map<string, number>();
      for (const product of products) {
        if (!productMatchesIntent(product, intent)) continue;
        const key = productSearchKey(product);
        // A generic shopping noun must describe the actual item, not merely its
        // category or ingredient. A known price is useful, never fabricated.
        scores.set(key, (lexical.get(key) ?? 10_000) + (product.new_price_mdl !== null ? 100 : 0));
      }
      return { scores, usedFuzzy: false };
    }
    const primaryMatches = runSearch(index, variants.slice(0, 1), false);
    if (primaryMatches.size > 0) {
      return { scores: primaryMatches, usedFuzzy: false };
    }
    const alternateMatches = runSearch(index, variants.slice(1), false);
    if (alternateMatches.size > 0) {
      return { scores: alternateMatches, usedFuzzy: false };
    }
    return {
      scores: runSearch(index, variants, true),
      usedFuzzy: true,
    };
  } catch (error) {
    if (!fallbackWarningShown) {
      fallbackWarningShown = true;
      console.warn("MiniSearch is unavailable; using exact catalog search.", error);
    }
    const intent = resolveSearchIntent(query);
    if (intent) return {
      scores: new Map(products.filter(product => productMatchesIntent(product, intent)).map(product => [productSearchKey(product), 1])),
      usedFuzzy: false,
    };
    return fallbackSearch(products, query);
  }
}

export function searchProducts(products: readonly ProductView[], query: string): ProductSearchMatches {
  const parsed = parseProductSearchQuery(query, products);
  if (parsed.error || !parsed.items.length) return { scores: new Map(), usedFuzzy: false };
  const cache = queryCaches.get(products) ?? new Map<string, ProductSearchMatches>();
  queryCaches.set(products, cache);
  const key = parsed.items.map(normalizeSearch).join("\u0000");
  const cached = cache.get(key);
  if (cached) return cached;
  let result: ProductSearchMatches;
  if (!parsed.multiple) result = searchSingleProduct(products, parsed.items[0]!);
  else {
    const scores = new Map<string, number>();
    let usedFuzzy = false;
    parsed.items.forEach((item, index) => {
      const matches = searchProducts(products, item);
      usedFuzzy ||= matches.usedFuzzy;
      matches.scores.forEach((score, id) => {
        if (!scores.has(id)) scores.set(id, score + (parsed.items.length - index) * 100_000);
      });
    });
    result = { scores, usedFuzzy };
  }
  if (cache.size >= 64) cache.delete(cache.keys().next().value!);
  cache.set(key, result);
  return result;
}

export function resetProductSearchCache(): void {
  queryCaches = new WeakMap();
  searchIndexes = new WeakMap();
  pendingSearchIndexes = new WeakMap();
  fallbackWarningShown = false;
}
